import type { BoardDetails } from '../interfaces/BoardDetails';

export type BoardTimerState = {
  hasTimer: boolean;
  isRunning: boolean;
  isStopped: boolean;
  isExpired: boolean;
  remainingSeconds: number;
};

const parseTimerDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const getBoardTimerState = (
  boardDetails: Pick<
    BoardDetails,
    'timerStartedAt' | 'timerStoppedAt' | 'timerDurationSeconds' | 'timerStatus'
  >,
  now = Date.now()
): BoardTimerState => {
  const startedAt = parseTimerDate(boardDetails.timerStartedAt);
  const stoppedAt = parseTimerDate(boardDetails.timerStoppedAt);
  const durationSeconds =
    typeof boardDetails.timerDurationSeconds === 'number' ? boardDetails.timerDurationSeconds : 0;
  const hasTimer = startedAt !== null && durationSeconds > 0;

  if (!hasTimer || startedAt === null) {
    return {
      hasTimer: false,
      isRunning: false,
      isStopped: false,
      isExpired: false,
      remainingSeconds: 0,
    };
  }

  const status = boardDetails.timerStatus ?? 'running';
  const referenceTime = status === 'stopped' && stoppedAt ? stoppedAt : now;
  const elapsedSeconds = Math.floor((referenceTime - startedAt) / 1000);
  const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
  const isStopped = status === 'stopped';

  return {
    hasTimer: true,
    isRunning: status === 'running' && remainingSeconds > 0,
    isStopped,
    isExpired: !isStopped && remainingSeconds === 0,
    remainingSeconds,
  };
};

export const formatBoardTimerSeconds = (seconds: number) => {
  const normalizedSeconds = Math.max(0, Math.floor(seconds));
  const days = Math.floor(normalizedSeconds / 86400);
  const hours = Math.floor((normalizedSeconds % 86400) / 3600);
  const minutes = Math.floor((normalizedSeconds % 3600) / 60);
  const remainderSeconds = normalizedSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainderSeconds.toString().padStart(2, '0')}`;
  }

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainderSeconds
      .toString()
      .padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${remainderSeconds
    .toString()
    .padStart(2, '0')}`;
};
